import React, {useEffect, useState, useRef} from 'react';
import clinicService from '../../../../../services/clinic.service';
import {useParams} from 'react-router-dom';
import {Pagination,  Alert, Button, Collapse, Form} from 'react-bootstrap';
import Loading from "../../../../common/Loading";
import Swal from 'sweetalert2';

function DocumentTab() {
    const {clinicId} = useParams();
    const [documentList, setDocumentList] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const [copiedItem, setCopiedItem] = useState(null);

    // Upload state
    const [showUploadSection, setShowUploadSection] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [documentType, setDocumentType] = useState('');
    const [dragActive, setDragActive] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const fileInputRef = useRef(null);
    const dropAreaRef = useRef(null);

    // Available page sizes
    const pageSizeOptions = [10, 20, 50];

    const documentTypes = [
        {value: 'EYECONX_COMM_TOOL_AGREEMENT', label: 'EyeconX Communication Tool Agreement'},
        {value: 'EYECONX_SERVICE_AGREEMENT', label: 'EyeconX Service Agreement'},
        {value: 'EYECONX_DATA_EXTRACTION_USE_AGREEMENT', label: 'EyeconX Data Extraction and Use Agreement'},
        {value: 'EYECONX_MONTH_TO_MONTH_AGREEMENT', label: 'EyeconX Month-To-Month Agreement'},
        {value: 'ER_EYECONX_COMM_TOOL_AGREEMENT', label: 'ER EyeconX Tool Agreement'},
        {value: 'ER_EYECONX_SERVICE_AGREEMENT', label: 'ER EyeconX Service Agreement'},
        {value: 'ER_EYECONX_DATA_EXTRACTION_USE_AGREEMENT', label: 'ER EyeconX Data Extraction and Use Agreement'},
        {value: 'EYECONX_SMS_GATEWAY_AGREEMENT', label: 'EyeconX SMS Gateway Agreement'},
        {value: 'INVOICE', label: 'Invoice'},
        {value: 'OTHER', label: 'Other'}
    ];

    // Supported file types and their extensions
    const supportedFileTypes = {
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/png': ['.png'],
        'text/plain': ['.txt']
    };

    const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.txt'];

    useEffect(() => {
        getDocumentsData();
    }, [clinicId, currentPage, itemsPerPage]);

    const getDocumentsData = async () => {
        try {
            setLoading(true);
            setError(null);

            const pageNumber = currentPage - 1;
            const res = await clinicService.getDocuments(clinicId, pageNumber, itemsPerPage);

            setDocumentList(res.data.content);
            setTotalElements(res.data.totalElements);
        } catch (err) {
            console.error(err);
            setError('Failed to load documents. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Validate file type and extension
    const validateFile = (file) => {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        // Check if extension is allowed
        if (!allowedExtensions.includes(fileExtension)) {
            return {
                isValid: false,
                error: `File type not supported. Allowed formats: ${allowedExtensions.join(', ')}`
            };
        }

        // Check if file type matches the extension
        if (!Object.values(supportedFileTypes).flat().includes(fileExtension)) {
            return {
                isValid: false,
                error: `File extension doesn't match the file type.`
            };
        }

        return { isValid: true };
    };

    // Delete document function
    const handleDeleteDocument = async (document) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `You are about to delete "${document.fileName}". This action cannot be undone!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Yes, delete it!`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            reverseButtons: true,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-danger btn-sm me-2',
                cancelButton: 'btn btn-secondary btn-sm me-2'
            },
            buttonsStyling: false,
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    await clinicService.deleteDocumentById(clinicId, document.id);
                    return { success: true };
                } catch (error) {
                    return { success: false, error: error.message };
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });

        if (result.isConfirmed) {
            if (result.value?.success) {
                // Show success animation
                await Swal.fire({
                    title: 'Deleted!',
                    text: `"${document.fileName}" has been deleted successfully.`,
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                    reverseButtons: true,
                    customClass: {
                        title: 'card-title',
                        confirmButton: 'btn btn-danger btn-sm me-2',
                    },
                    buttonsStyling: false,
                    willClose: () => {
                        // Refresh the documents list
                        getDocumentsData();
                    }
                });
            } else {
                // Show error message
                Swal.fire({
                    title: 'Error!',
                    text: `Failed to delete document. ${result.value?.error || 'Please try again.'}`,
                    icon: 'error',
                    confirmButtonColor: '#d33',
                    customClass: {
                        title: 'card-title',
                        text: 'small',
                        confirmButton: 'btn btn-danger btn-sm me-2',
                    }
                });
            }
        }
    };

    // Upload section handlers
    const toggleUploadSection = () => {
        setShowUploadSection(!showUploadSection);
        if (!showUploadSection) {
            // Reset form when opening
            setSelectedFile(null);
            setDocumentType('');
            setDragActive(false);
        }
    };

    // Drag and drop handlers
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];

            // Validate file size
            if (file.size > 32 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'File too large',
                    text: 'File size must be less than 32MB',
                    customClass: {
                        title: 'card-title',
                        text: 'small',
                        confirmButton: 'btn btn-danger btn-sm me-2',
                    }
                });
                return;
            }

            // Validate file type
            const validation = validateFile(file);
            if (!validation.isValid) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File Format',
                    text: validation.error,
                    customClass: {
                        title: 'card-title',
                        text: 'small',
                        confirmButton: 'btn btn-danger btn-sm me-2',
                    }
                });
                return;
            }

            setSelectedFile(file);
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            // Validate file size
            if (file.size > 32 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'File too large',
                    text: 'File size must be less than 32MB',
                    customClass: {
                        title: 'card-title',
                        text: 'small',
                        confirmButton: 'btn btn-danger btn-sm me-2',
                    }
                });
                e.target.value = ''; // Reset file input
                return;
            }

            // Validate file type
            const validation = validateFile(file);
            if (!validation.isValid) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File Format',
                    text: validation.error,
                    customClass: {
                        title: 'card-title',
                        text: 'small',
                        confirmButton: 'btn btn-danger btn-sm me-2',
                    }
                });
                e.target.value = ''; // Reset file input
                return;
            }

            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing File',
                text: 'Please select a file to upload',
                customClass: {
                    title: 'card-title',
                    text: 'small',
                    confirmButton: 'btn btn-success btn-sm me-2',
                }
            });
            return;
        }

        if (!documentType) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Document Type',
                text: 'Please select a document type',
                customClass: {
                    title: 'card-title',
                    text: 'small',
                    confirmButton: 'btn btn-success btn-sm me-2',
                }
            });
            return;
        }

        // Final validation before upload
        const validation = validateFile(selectedFile);
        if (!validation.isValid) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid File Format',
                text: validation.error,
                customClass: {
                    title: 'card-title',
                    text: 'small',
                    confirmButton: 'btn btn-danger btn-sm me-2',
                }
            });
            return;
        }

        try {
            setUploading(true);

            // Show loading SweetAlert
            Swal.fire({
                title: 'Uploading...',
                text: 'Please wait while we upload your document',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            // Create FormData for file upload
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('type', documentType);

            // Call your upload API
            await clinicService.uploadDocument(clinicId, formData);

            // Refresh the documents list
            await getDocumentsData();

            // Close upload section
            setShowUploadSection(false);
            setSelectedFile(null);
            setDocumentType('');

            // Show success message
            Swal.fire({
                icon: 'success',
                title: 'Upload Successful!',
                text: 'Your document has been uploaded successfully',
                customClass: {
                    title: 'card-title',
                    text: 'small',
                    confirmButton: 'btn btn-success btn-sm me-2',
                }
            });

        } catch (err) {
            console.error('Upload error:', err);

            // Handle different types of upload errors
            let errorMessage = 'Failed to upload document. Please try again.';

            if (err.response) {
                // Server responded with error status
                if (err.response.status === 400) {
                    errorMessage = 'Invalid file format or file is corrupted. Please check the file and try again.';
                } else if (err.response.status === 413) {
                    errorMessage = 'File is too large. Please select a file smaller than 32MB.';
                } else if (err.response.status === 415) {
                    errorMessage = 'Unsupported file type. Please upload a supported file format.';
                } else if (err.response.data && err.response.data.message) {
                    errorMessage = err.response.data.message;
                }
            } else if (err.request) {
                // Network error
                errorMessage = 'Network error. Please check your connection and try again.';
            }

            Swal.fire({
                icon: 'error',
                title: 'Upload Failed',
                text: errorMessage,
                customClass: {
                    title: 'card-title',
                    text: 'small',
                    confirmButton: 'btn btn-danger btn-sm me-2',
                }
            });

        } finally {
            setUploading(false);
            Swal.close();
        }
    };

    const handleCancelUpload = () => {
        if (selectedFile || documentType) {
            Swal.fire({
                title: 'Cancel Upload?',
                text: 'Are you sure you want to cancel? Any unsaved changes will be lost.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: `<i class="bi bi-check"></i> Yes, cancel it!`,
                cancelButtonText: `<i class="bi bi-x"></i> No, keep editing`,
                reverseButtons: true,
                customClass: {
                    title: 'card-title',
                    confirmButton: 'btn btn-danger btn-sm me-2',
                    cancelButton: 'btn btn-secondary btn-sm me-2'
                },
                buttonsStyling: false
            }).then((result) => {
                if (result.isConfirmed) {
                    setShowUploadSection(false);
                    setSelectedFile(null);
                    setDocumentType('');
                    setDragActive(false);
                }
            });
        } else {
            setShowUploadSection(false);
            setSelectedFile(null);
            setDocumentType('');
            setDragActive(false);
        }
    };

    const copyToClipboard = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedItem({id, type: 'id'});

        // Show SweetAlert instead of inline indicator
        Swal.fire({
            icon: 'success',
            title: 'Copied!',
            text: 'Document ID copied to clipboard',
            timer: 1500,
            showConfirmButton: false,
            position: 'top-end',
            toast: true
        });

        setTimeout(() => setCopiedItem(null), 2000);
    };

    const toggleRowExpand = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleDownload = (clinicId, docId, fileName) => {
        clinicService.getDocumentById(clinicId, docId)
            .then(response => {
                const blob = new Blob([response.data], {type: response.headers['content-type']})
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = fileName
                link.click()
                window.URL.revokeObjectURL(url)
            })
            .catch(err => {
                console.log(err)
                Swal.fire({
                    icon: 'error',
                    title: 'Download Failed',
                    text: 'Failed to download document. Please try again.',
                    customClass: {
                        title: 'card-title',
                        text: 'small',
                        confirmButton: 'btn btn-danger btn-sm me-2',
                    }
                });
            })
    }

    const handleView = (document) => {
        window.open(document.filePath, '_blank');
    };

    const formatDocumentType = (type) => {
        return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const totalPages = Math.ceil(totalElements / itemsPerPage);

    let paginationItems = [];
    for (let number = 1; number <= totalPages; number++) {
        paginationItems.push(
            <Pagination.Item
                key={number}
                active={number === currentPage}
                onClick={() => setCurrentPage(number)}
            >
                {number}
            </Pagination.Item>
        );
    }

    return (
        <div className="card card-padding shadow-none">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="card-title mb-0">
                        <i className="bi bi-files"></i> Documents
                    </div>
                    <Button
                        variant={showUploadSection ? "danger" : "outline-primary"}
                        onClick={toggleUploadSection}
                        className="d-flex align-items-center btn-sm"
                    >
                        <i className={`bi ${showUploadSection ? 'bi-x-square' : 'bi-cloud-upload'} me-1`}></i>
                        {showUploadSection ? 'Cancel' : 'Upload Document'}
                    </Button>
                </div>

                {/* Upload Section - Displayed inline */}
                {showUploadSection && (
                    <div className="card mb-4 shadow-none card-border small">
                        <div className="card-header">
                            <i className="bi bi-cloud-upload me-2"></i>
                            Upload New Document
                        </div>
                        <div className="card-body card-padding">
                            <div className="row">
                                <div className="col-md-12">
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Document Type</Form.Label>
                                        <Form.Select
                                            value={documentType}
                                            onChange={(e) => setDocumentType(e.target.value)}
                                            disabled={uploading}
                                        >
                                            <option value="">Select document type...</option>
                                            {documentTypes.map(type => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Select File</Form.Label>
                                        <div
                                            ref={dropAreaRef}
                                            className={`border-2 border-dashed rounded p-4 text-center ${
                                                dragActive ? 'border-primary bg-light' : 'border-secondary'
                                            } ${selectedFile ? 'border-success bg-light' : ''}`}
                                            onDragEnter={handleDrag}
                                            onDragLeave={handleDrag}
                                            onDragOver={handleDrag}
                                            onDrop={handleDrop}
                                            style={{cursor: 'pointer', minHeight: '120px'}}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                className="d-none"
                                                onChange={handleFileSelect}
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                                                disabled={uploading}
                                            />

                                            {selectedFile ? (
                                                <div>
                                                    <i className="bi bi-file-earmark-check text-success fs-1"></i>
                                                    <div className="mt-2">
                                                        <strong>{selectedFile.name}</strong>
                                                    </div>
                                                    <div className="text-muted small">
                                                        {formatFileSize(selectedFile.size)}
                                                    </div>
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        className="mt-2"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedFile(null);
                                                        }}
                                                    >
                                                        Change File
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div>
                                                    <i className="bi bi-cloud-upload text-muted fs-1"></i>
                                                    <div className="mt-2">
                                                        <strong>Click to select or drag and drop</strong>
                                                    </div>
                                                    <div className="text-muted small">
                                                        Supports PDF, DOC, DOCX, JPG, PNG, TXT (Max 32MB)
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Form.Group>
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-2 mt-3">
                                <Button
                                    variant="danger"
                                    onClick={handleCancelUpload}
                                    disabled={uploading}
                                    className="btn-sm"
                                >
                                    <i className="bi bi-x me-1"></i>
                                    Cancel
                                </Button>
                                <Button
                                    variant="success"
                                    onClick={handleUpload}
                                    disabled={uploading || !selectedFile || !documentType}
                                    className="btn-sm"
                                >
                                    {uploading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"
                                                  role="status"></span>
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-cloud-upload me-2"></i>
                                            Upload Document
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {error && (
                    <Alert variant="danger" className="mt-3">
                        {error}
                    </Alert>
                )}

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                        <span className="me-2">Show</span>
                        <select
                            className="form-select form-select-sm"
                            style={{width: '70px'}}
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            {pageSizeOptions.map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                        <span className="ms-2">entries</span>
                    </div>
                    <div>
                        Total documents: <strong>{totalElements}</strong>
                    </div>
                </div>

                {loading ? (
                    <Loading/>
                ) : (
                    <>
                        <div className="card-padding shadow-none table-responsive">
                            <table className="table table-bordered table-hover mb-0 small">
                                <thead className="table-light">
                                <tr>
                                    <th className="col-4"><i className="bi bi-file-earmark"></i> File Name</th>
                                    <th className="col-3"><i className="bi bi-tags"></i> Type</th>
                                    <th className="col-3"><i className="bi bi-calendar-plus"></i> Created</th>
                                    <th className="col-2"><i className="bi bi-gear"></i> Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {documentList.length > 0 ? (
                                    documentList.map((document) => (
                                        <React.Fragment key={document.id}>
                                            <tr>
                                                <td>
                                                    <div className="fw-normal">{document.fileName}</div>
                                                </td>
                                                <td className="fw-bolder">
                                                    {formatDocumentType(document.documentType)}
                                                </td>
                                                <td className="">{document.createdAt}</td>
                                                <td className="text-center">
                                                    <div className="d-flex gap-1 justify-content-center">
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            onClick={() => handleDownload(clinicId, document.id, document.fileName)}
                                                            title="Download Document"
                                                        >
                                                            <i className="bi bi-download"></i>
                                                        </Button>
                                                        <button
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => toggleRowExpand(document.id)}
                                                            aria-controls={`collapse-details-${document.id}`}
                                                            aria-expanded={expandedRows[document.id]}
                                                        >
                                                            {expandedRows[document.id] ?
                                                                <i className="bi bi-chevron-up"></i> :
                                                                <i className="bi bi-chevron-down"></i>
                                                            }
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colSpan="4" className="p-0">
                                                    <Collapse in={expandedRows[document.id]}>
                                                        <div id={`collapse-details-${document.id}`}
                                                             className="p-3 bg-light">
                                                            <div className="row">
                                                                <div className="col-md-12">
                                                                    <div className="mb-2">
                                                                        <strong>File Path:</strong>
                                                                        <div className="small text-break">
                                                                            <a href={document.filePath} target="_blank"
                                                                               rel="noopener noreferrer">
                                                                                {document.filePath}
                                                                            </a>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <div className="mb-2 d-flex align-items-center">
                                                                        <strong className="me-2">Document ID:</strong>
                                                                        <span className="small font-monospace">
                                                                            {document.id}
                                                                        </span>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-secondary py-0 px-1 ms-2"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                copyToClipboard(document.id, document.id);
                                                                            }}
                                                                            title="Copy ID"
                                                                        >
                                                                            <i className="bi bi-copy"></i>
                                                                        </button>
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <strong>Clinic ID:</strong> {document.clinicId}
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <strong>File
                                                                            Size:</strong> {formatFileSize(document.fileSize)}
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <strong>Created:</strong> {document.createdAt}
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <strong>Last
                                                                            Modified:</strong> {document.modifiedAt}
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <div
                                                                        className="d-flex gap-2 justify-content-end align-content-center mt-3">
                                                                        <Button
                                                                            variant="success"
                                                                            size="sm"
                                                                            onClick={() => handleDownload(clinicId, document.id, document.fileName)}
                                                                        >
                                                                            <i className="bi bi-download me-1"></i> Download
                                                                        </Button>
                                                                        <Button
                                                                            variant="danger"
                                                                            size="sm"
                                                                            onClick={() => handleDeleteDocument(document)}
                                                                            title="Delete Document"
                                                                        >
                                                                            <i className="bi bi-trash me-1"></i> Delete
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Collapse>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="text-center py-4">
                                            {error ? (
                                                <Alert variant="danger" className="mb-0">
                                                    {error}
                                                </Alert>
                                            ) : (
                                                "No documents found"
                                            )}
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>

                        {documentList.length > 0 && (
                            <div className="d-flex justify-content-between mt-3">
                                <div>
                                    Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                                    {Math.min(currentPage * itemsPerPage, totalElements)} of {totalElements} entries
                                </div>

                                <Pagination>
                                    <Pagination.First
                                        onClick={() => setCurrentPage(1)}
                                        disabled={currentPage === 1}
                                    />
                                    <Pagination.Prev
                                        onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                                        disabled={currentPage === 1}
                                    />

                                    {paginationItems}

                                    <Pagination.Next
                                        onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    />
                                    <Pagination.Last
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default DocumentTab;